import { Box } from "@mui/material"
import {
  FocusContext,
  FocusHandler,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useEffect, useState } from "react"

import useSdk, { ErrorLevel } from "../../../../../hooks/use-sdk"
import useTranslate from "../../../../../hooks/use-translate"
import { useDetailStore } from "../../../../../screens/Detail/DetailStore"
import { appStore } from "../../../../../store/AppStore"
import {
  isFavoritedContent,
  TPlaylistItem,
} from "../../../../../store/PlayerStore"
import { comb, secondsToTime } from "../../../../../utilities/common"
import { s } from "../../../../../utilities/common"
import { theme, TI } from "../../../../../utilities/theme"
import { DGText } from "../../../../common/DGText"
import { PlaylistFocusKeys } from "../playlist-focus-keys"

import { PauseIcon } from "./PauseIcon"
import { PlayIcon } from "./PlayIcon"
import { PlaylistStarIcon } from "./PlaylistStarIcon"

export type TColumn = "item" | "icon"

type PlaylistItemProps = {
  index: number
  data: TPlaylistItem
  paused: boolean
  onPress: (id: string) => void
  selected?: boolean
  me: string
  onFocus?: FocusHandler
  hasFavoriteButton?: boolean
  isFavorite?: boolean
}
export const PlaylistItem = ({
  data,
  selected,
  me,
  hasFavoriteButton = false,
  column,
  ...rest
}: PlaylistItemProps & { column?: comb<TColumn> }) => {
  const { ref, focusSelf, focusKey, hasFocusedChild } = useFocusable({
    focusKey: me + PlaylistFocusKeys.Item + data.id,
    saveLastFocusedChild: false,
    preferredChildFocusKey:
      column !== undefined
        ? column.val === "item"
          ? me + PlaylistFocusKeys.Entry + data.id
          : me + PlaylistFocusKeys.Icon + data.id
        : undefined,
    trackChildren: true,
  })

  useEffect(() => {
    if (selected && ref.current) focusSelf()
  }, [])

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{
          mb: "1px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "stretch",
        }}
      >
        <PlaylistEntry
          parentFocused={hasFocusedChild}
          data={data}
          selected={selected}
          me={me}
          column={column}
          {...rest}
        />
        {hasFavoriteButton ? (
          <FavoriteIcon
            parentFocused={hasFocusedChild}
            data={data}
            me={me}
            selected={selected}
            column={column}
            {...rest}
          />
        ) : null}
      </Box>
    </FocusContext.Provider>
  )
}

const PlaylistEntry = ({
  onPress,
  selected,
  me,
  index,
  onFocus,
  data,
  paused,
  parentFocused,
  column,
}: PlaylistItemProps & { parentFocused: boolean; column?: comb<TColumn> }) => {
  const onPressAction = () => {
    onPress(data.id)
  }
  const { ref, focused, focusSelf } = useFocusable({
    focusKey: me + PlaylistFocusKeys.Entry + data.id,
    onFocus: (x, _, z) => {
      if (onFocus) onFocus(x, {}, z)
      if (column) column.set("item")
    },
    onEnterPress: onPressAction,
  })
  const t = useTranslate()

  return (
    <Box
      tabIndex={0}
      aria-label={`
      ${t("metaInfoTrackNumber", [index + 1])}: 
      ${data.composer ?? ""}. 
      ${data.title}. 
      ${data.subtitle ?? ""}. 
      ${t("metaInfoLabel__track_duration", [secondsToTime(data.duration)])}`}
      onMouseDown={onPressAction}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      ref={ref}
      sx={{
        width: s("900px"),
        pr: s("40px"),
        display: "flex",
        flexDirection: "row",
        minHeight: s("147px"),
        borderBottom: "1px solid",
        borderTop: index === 0 ? "1px solid" : undefined,
        borderColor: !(focused || parentFocused)
          ? theme.palette.white[20]
          : theme.palette.primary.main,
        borderRadius: focused || parentFocused ? s("16px") : undefined,
        backgroundColor: focused
          ? theme.palette.primary.variant
          : parentFocused
            ? theme.palette.primary.main
            : undefined,
        boxShadow: focused
          ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.6)`
          : parentFocused
            ? `${s("0px 4px 16px 0px")} rgba(0, 0, 0, 0.40)`
            : undefined,
        transform: focused
          ? "scale(1.14)"
          : parentFocused
            ? "scale(1.06)"
            : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: s("148px"),
        }}
      >
        <DGText
          tx={TI.Subheadline05tv}
          sx={{
            color: theme.palette.white[30],
          }}
        >
          {selected ? paused ? <PlayIcon /> : <PauseIcon /> : data.index}
        </DGText>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pt: s("36px"),
          pb: s("40px"),
        }}
      >
        {data.composer && (
          <DGText
            tx={TI.Subheadline08tv}
            sx={{
              color: theme.palette.white[30],
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 1,
            }}
          >
            {data.composer}
          </DGText>
        )}
        <DGText
          tx={TI.Subheadline02tv}
          sx={{
            color: theme.palette.white.main,
          }}
        >
          {data.title}
        </DGText>
        {data.subtitle ? (
          <DGText
            tx={TI.Subheadline05tv}
            sx={{
              color: theme.palette.white[70],
            }}
          >
            {data.subtitle}
          </DGText>
        ) : null}
        <DGText
          tx={TI.Subheadline08tv}
          sx={{
            mt: s("10px"),
            color: theme.palette.white[30],
          }}
        >
          {secondsToTime(data.duration)}
        </DGText>
      </Box>
    </Box>
  )
}

const FavoriteIcon = ({
  me,
  isFavorite,
  data,
  onFocus,
  parentFocused,
  column,
}: PlaylistItemProps & { parentFocused: boolean; column?: comb<TColumn> }) => {
  const sdk = useSdk(ErrorLevel.NoError)
  const myStage = appStore((state) => state.myStage)
  const content = useDetailStore((state) => state.content)
  const setMyStage = appStore((state) => state.setMyStage)
  const onPressAction = async () => {
    setMarked(!marked)
    if (!marked) sdk.addFavorite({ input: { contentId: data.id } })
    if (marked) sdk.removeFavoriteByContentId({ input: { contentId: data.id } })
    const result = await sdk.myStagePage({
      sliderPageSize: 7,
      cursor: undefined,
    })
    setMyStage(result)
  }
  const { ref, focused, focusSelf } = useFocusable({
    focusKey: me + PlaylistFocusKeys.Icon + data.id,
    onFocus: (x, _, z) => {
      if (onFocus) onFocus(x, {}, z)
      if (column) column.set("icon")
    },
    onEnterPress: onPressAction,
  })
  const t = useTranslate()

  const [marked, setMarked] = useState(false)
  useEffect(() => {
    setMarked(isFavorite ?? false)
  }, [isFavorite])

  useEffect(() => {
    setMarked(isFavoritedContent(myStage, content, data.id))
  }, [myStage, content])

  return (
    <Box
      tabIndex={0}
      aria-label={
        marked
          ? t("contextmenu__remove_favorite")
          : t("component__button_stack_fav")
      }
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        onMouseDown={onPressAction}
        onMouseEnter={() => focusSelf({ noScroll: true })}
        sx={{
          p: s("15px"),
          background: focused
            ? "white"
            : parentFocused
              ? theme.palette.white[70]
              : "none",
          borderRadius: s("8px"),
          boxShadow: focused
            ? "0px 30px 112px 0px rgba(0, 0, 0, 0.6)"
            : parentFocused
              ? "0px 4px 16px 0px rgba(0, 0, 0, 0.40)"
              : undefined,
          transform: focused
            ? "scale(1.14)"
            : parentFocused
              ? "scale(1.06)"
              : undefined,
        }}
      >
        <PlaylistStarIcon
          focused={focused || parentFocused}
          isFavorite={marked}
        />
      </Box>
    </Box>
  )
}
